import React from 'react';
import * as Sentry from '@sentry/react';
import { captureException } from '../utils/sentry';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log to console
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Report to Sentry
    captureException(error, {
      extra: {
        componentStack: errorInfo.componentStack,
        errorInfo: JSON.stringify(errorInfo)
      }
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="max-w-md p-8 bg-card rounded-lg shadow-lg">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h1>
            <pre className="text-sm bg-muted p-4 rounded overflow-auto max-h-[200px]">
              {this.state.error?.message}
            </pre>
            <div className="flex gap-4 mt-4">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
              >
                Reload Page
              </button>
              <button 
                onClick={() => Sentry.showReportDialog()}
                className="px-4 py-2 bg-secondary text-secondary-foreground rounded hover:bg-secondary/90"
              >
                Report Feedback
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default Sentry.withProfiler(ErrorBoundary, { name: 'ErrorBoundary' });
